// =============================================================================
// TOAST NOTIFICATION UTILITIES
// =============================================================================

import { useToast } from '@/contexts/ToastContext';

/**
 * Enhanced toast hook with common notification patterns
 */
export function useToastNotifications() {
  const toast = useToast();

  // Success notifications
  const showSuccess = (message: string, title?: string) => {
    return toast.success(message, { title });
  };

  const showSaveSuccess = (item: string = 'Changes') => {
    return toast.success(`${item} saved successfully!`);
  };

  const showDeleteSuccess = (item: string = 'Item') => {
    return toast.success(`${item} deleted successfully!`);
  };

  const showUpdateSuccess = (item: string = 'Item') => {
    return toast.success(`${item} updated successfully!`);
  };

  // Error notifications
  const showError = (message: string, title?: string) => {
    return toast.error(message, { title });
  };

  const showSaveError = (item: string = 'Changes') => {
    return toast.error(`Failed to save ${item.toLowerCase()}. Please try again.`);
  };

  const showDeleteError = (item: string = 'Item') => {
    return toast.error(`Failed to delete ${item.toLowerCase()}. Please try again.`);
  };

  const showUpdateError = (item: string = 'Item') => {
    return toast.error(`Failed to update ${item.toLowerCase()}. Please try again.`);
  };

  const showNetworkError = () => {
    return toast.error('Network error. Please check your connection and try again.');
  };

  const showValidationError = (message: string) => {
    return toast.error(message, { title: 'Validation Error' });
  };

  // Warning notifications
  const showWarning = (message: string, title?: string) => {
    return toast.warning(message, { title });
  };

  const showUnsavedChanges = () => {
    return toast.warning('You have unsaved changes. Save before leaving?', {
      title: 'Unsaved Changes',
      duration: 0, // Persistent until dismissed
      action: {
        label: 'Save Now',
        onClick: () => {
          // This would be handled by the parent component
        }
      }
    });
  };

  // Info notifications
  const showInfo = (message: string, title?: string) => {
    return toast.info(message, { title });
  };

  const showComingSoon = (feature: string) => {
    return toast.info(`${feature} is coming soon!`, { title: 'Coming Soon' });
  };

  // Loading notifications
  const showLoading = (message: string, title?: string) => {
    return toast.loading(message, { title });
  };

  const showSaving = (item: string = 'Changes') => {
    return toast.loading(`Saving ${item.toLowerCase()}...`);
  };

  const showDeleting = (item: string = 'Item') => {
    return toast.loading(`Deleting ${item.toLowerCase()}...`);
  };

  const showUpdating = (item: string = 'Item') => {
    return toast.loading(`Updating ${item.toLowerCase()}...`);
  };

  // Form-specific notifications
  const showFormError = (field: string, message: string) => {
    return toast.error(`${field}: ${message}`, { title: 'Form Error' });
  };

  const showFormSuccess = (action: string) => {
    return toast.success(`Form ${action} successfully!`);
  };

  // Removed auth-specific notifications as site is now public-only

  // File upload notifications
  const showUploadSuccess = (filename: string) => {
    return toast.success(`File "${filename}" uploaded successfully!`);
  };

  const showUploadError = (filename?: string) => {
    const file = filename ? `"${filename}"` : 'file';
    return toast.error(`Failed to upload ${file}. Please try again.`);
  };

  const showUploadProgress = (filename: string, progress: number) => {
    return toast.loading(`Uploading "${filename}"... ${progress}%`);
  };

  // Generic utility methods
  const dismissToast = (id: string) => {
    toast.removeToast(id);
  };

  const clearAllToasts = () => {
    toast.clearAllToasts();
  };

  return {
    // Core toast methods
    ...toast,
    
    // Success methods
    showSuccess,
    showSaveSuccess,
    showDeleteSuccess,
    showUpdateSuccess,
    
    // Error methods
    showError,
    showSaveError,
    showDeleteError,
    showUpdateError,
    showNetworkError,
    showValidationError,
    
    // Warning methods
    showWarning,
    showUnsavedChanges,
    
    // Info methods
    showInfo,
    showComingSoon,
    
    // Loading methods
    showLoading,
    showSaving,
    showDeleting,
    showUpdating,
    
    // Form methods
    showFormError,
    showFormSuccess,
    
    // File upload methods
    showUploadSuccess,
    showUploadError,
    showUploadProgress,
    
    // Utility methods
    dismissToast,
    clearAllToasts,
  };
}
